/* eslint-disable no-console */
import {
  ArrowLeft,
  ArrowRight,
  ContentPaste,
  Crop,
  FeaturedVideo,
  Fullscreen,
  PanTool,
  ZoomIn,
  ZoomOut,
} from '@mui/icons-material';
import { IconButton, IconButtonProps, Tooltip } from '@mui/material';
import {
  Fragment,
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactCropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { CSSObject, styled } from '@mui/system';
import { toast } from 'react-toastify';
import CropperJs from 'cropperjs';

interface CropperProps {
  images: Array<string>;
  disabled?: boolean;
  onCut?: (blob: Blob | null, indexImage?: number) => void;
  height?: number;
}

interface CustomIconButtonProps extends IconButtonProps {
  active?: boolean;
}

export const CustomIconButton = styled(IconButton, {
  shouldForwardProp: (props) => {
    if (props === 'active') {
      return false;
    }

    return true;
  },
})<CustomIconButtonProps>((props) => {
  const style: CSSObject = {
    backgroundColor: 'transparent',
    pointerEvents: 'auto',
  };

  if (props.active && !props.disabled) {
    style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
    style.pointerEvents = 'none';
  }

  return style;
});

enum CropperEnum {
  move = 0,
  crop = 1,
}

const CropperComponent = ({
  images,
  disabled,
  onCut,
  height = 800,
}: CropperProps): ReactElement => {
  const [stateImages, setImages] = useState<number | undefined>();

  const indexImage = useMemo(() => stateImages || 0, [stateImages]);

  const hasImages = useMemo(() => images.length > 0, [images]);

  const [activeButton, setActiveButton] = useState<number>(CropperEnum.move);

  const [reference, setReference] = useState<CropperJs>();

  const ref = useRef<HTMLDivElement | null>(null);

  const currentReference = reference as CropperJs;

  useEffect(() => {
    setImages(0);
  }, []);

  const zoomIn = useCallback(() => {
    if (!hasImages) {
      return;
    }
    currentReference?.zoom(0.1);
  }, [currentReference, hasImages]);

  const zoomOut = useCallback(() => {
    if (!hasImages) {
      return;
    }
    currentReference?.zoom(-0.1);
  }, [currentReference, hasImages]);

  const crop = useCallback(() => {
    if (!hasImages) {
      return;
    }

    const pngBlob = currentReference?.crop().getCroppedCanvas();

    if (!pngBlob) {
      return;
    }

    pngBlob.toBlob(async (result) => {
      const type = result?.type || '';
      try {
        if (onCut) {
          onCut(result, indexImage);
        }

        await window.navigator.clipboard.write([
          new ClipboardItem({
            [type]: result as ClipboardItemDataType,
          }),
        ]);
        toast.success('Elemento copiato con successo');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    });
  }, [currentReference, hasImages, indexImage]);

  return (
    <Fragment>
      <div className="pmi-d-flex">
        <div className="pmi-d-flex pmi-flex-column">
          <Tooltip arrow title="Trascina">
            <CustomIconButton
              onClick={() => {
                currentReference?.clear();
                currentReference?.setDragMode('move');
                setActiveButton(CropperEnum.move);
              }}
              active={activeButton === CropperEnum.move}
              disabled={disabled}
            >
              <PanTool />
            </CustomIconButton>
          </Tooltip>
          <Tooltip arrow title="Ingrandisci">
            <IconButton onClick={zoomIn} disabled={disabled}>
              <ZoomIn />
            </IconButton>
          </Tooltip>

          <Tooltip arrow title="Riduci">
            <IconButton onClick={zoomOut} disabled={disabled}>
              <ZoomOut />
            </IconButton>
          </Tooltip>

          <Tooltip arrow title="Seleziona ritaglio">
            <CustomIconButton
              disabled={disabled}
              onClick={() => {
                currentReference?.setDragMode('crop');
                setActiveButton(CropperEnum.crop);
              }}
              active={activeButton === CropperEnum.crop}
            >
              <Crop />
            </CustomIconButton>
          </Tooltip>

          <Tooltip arrow title="Zoom Atto intero">
            <IconButton
              disabled={disabled}
              onClick={() => {
                if (!hasImages) {
                  return;
                }

                currentReference?.setCanvasData({
                  top: 0,
                  left: 0,
                  width: (ref.current?.clientWidth || 0) / 2,
                  height: (ref.current?.scrollHeight || 0) / 2,
                });
              }}
            >
              <FeaturedVideo />
            </IconButton>
          </Tooltip>

          <Tooltip arrow title="Zoom Atto in larghezza">
            <IconButton
              disabled={disabled}
              onClick={() => {
                if (!hasImages) {
                  return;
                }

                currentReference?.setCanvasData({
                  top: 0,
                  left: 0,
                  width: ref.current?.clientWidth || 0,
                  height: ref.current?.scrollHeight || 0,
                });
              }}
            >
              <Fullscreen />
            </IconButton>
          </Tooltip>

          <Tooltip arrow title="Memorizza ritaglio">
            <IconButton
              disabled={disabled}
              onClick={() => {
                crop();
              }}
            >
              <ContentPaste />
            </IconButton>
          </Tooltip>

          <Tooltip arrow title="Pagina precedente">
            <IconButton
              onClick={() => {
                if (indexImage <= 0) {
                  return;
                }
                setImages(indexImage - 1);
              }}
              disabled={indexImage === 0 || disabled}
            >
              <ArrowLeft />
            </IconButton>
          </Tooltip>

          <Tooltip arrow title="Pagina successiva">
            <IconButton
              onClick={() => {
                if (indexImage >= images.length - 1) {
                  return;
                }
                setImages(indexImage + 1);
              }}
              disabled={indexImage === images.length - 1 || disabled}
            >
              <ArrowRight />
            </IconButton>
          </Tooltip>
        </div>
        {!hasImages ? undefined : (
          <div ref={ref} style={{ flexGrow: 1, width: '100%', height: height }}>
            <ReactCropper
              style={{ height: height, width: '100%' }}
              zoomTo={0}
              src={images[indexImage]}
              dragMode={'move'}
              autoCrop={false}
              responsive={true}
              checkOrientation={false}
              background={false}
              guides
              viewMode={1}
              center={false}
              start={0}
              initialAspectRatio={1}
              onInitialized={(instance) => {
                setReference(instance);
              }}
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};

CropperComponent.displayName = 'Cropper';

export const Cropper = memo(CropperComponent);
